// Canvas 2D rendering of a sticker (shares shape paths + font strings with the SVG view), the print sheet, the cut file and downloads.
import { cutLoops, loopsPath, fontString, textLines, shapePath, sheetLayout, regMarks, tileBox } from "./stickerShapes";
import { materialOf, typeOf, FINISHES, fmtIn } from "./stickerModel";
import { api } from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const imgCache = new Map();
export const loadImage = (url) => {
  const full = url.startsWith("http") ? url : `${API}${url}`;
  if (!imgCache.has(full)) imgCache.set(full, new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = () => rej(new Error("image failed")); im.src = full; }));
  return imgCache.get(full);
};
export const loadFonts = async (doc) => {
  const fams = [...new Set(doc.layers.filter((l) => l.kind === "text").map((l) => fontString(l, 40)))];
  await Promise.all(fams.map((fs) => document.fonts.load(fs).catch(() => null)));
};

// Draw the artwork layers in inches (ctx already scaled to dpi and translated to the sticker origin).
export const drawLayers = async (ctx, doc, { artOnly = false } = {}) => {
  for (const l of doc.layers) {
    if (l.hidden) continue;
    ctx.save();
    ctx.globalAlpha = l.opacity ?? 1;
    ctx.translate(l.x, l.y);
    ctx.rotate(((l.rot || 0) * Math.PI) / 180);
    if (l.kind === "image") {
      try { const im = await loadImage(l.url); ctx.drawImage(im, -l.w / 2, -l.h / 2, l.w, l.h); } catch { /* missing art */ }
    } else if (l.kind === "shape") {
      const p = new Path2D(shapePath(l.shape, l.w, l.h, l.radius));
      ctx.fillStyle = l.fill; ctx.fill(p);
      if (l.strokeWidth > 0) { ctx.lineWidth = l.strokeWidth; ctx.lineJoin = "round"; ctx.strokeStyle = l.stroke; ctx.stroke(p); }
    } else if (l.kind === "text") {
      // glyphs are laid out in device pixels (a 1.2 px font scaled ×300 quantizes advances) — undo the inch scale for text
      const k = ctx.__ppi || 1;
      ctx.scale(1 / k, 1 / k);
      ctx.font = fontString(l, l.size * k);
      try { ctx.letterSpacing = `${(l.spacing || 0) * k}px`; } catch { /* no-op */ }
      ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.lineJoin = "round";
      const lines = textLines(l), lh = l.size * (l.lineHeight || 1.1) * k;
      lines.forEach((t, i) => {
        const y = (i - (lines.length - 1) / 2) * lh;
        if (l.strokeWidth > 0) { ctx.lineWidth = l.strokeWidth * 2 * k; ctx.strokeStyle = l.stroke; ctx.strokeText(t, 0, y); }
        ctx.fillStyle = l.fill; ctx.fillText(t, 0, y);
      });
    }
    ctx.restore();
  }
  void artOnly;
};

// Alpha mask of the artwork for the contour trace: sticker box + 1″ margin, origin shifted by (1,1).
export const renderMask = async (doc, ppi = 40) => {
  const M = 1, cv = document.createElement("canvas");
  cv.width = Math.ceil((doc.w + 2 * M) * ppi); cv.height = Math.ceil((doc.h + 2 * M) * ppi);
  const ctx = cv.getContext("2d");
  ctx.__ppi = ppi;
  ctx.scale(ppi, ppi); ctx.translate(M, M);
  await loadFonts(doc);
  await drawLayers(ctx, doc, { artOnly: true });
  return { canvas: cv, margin: M };
};

// One printed tile: bleed-grown background, artwork, optional border line. Origin = tile box top-left. Returns nothing (draws into ctx).
export const drawTile = async (ctx, doc, { cutLine = false } = {}) => {
  const mat = materialOf(doc), loops = cutLoops(doc), path = new Path2D(loopsPath(loops)), tile = tileBox(doc);
  ctx.save();
  ctx.translate(-tile.x, -tile.y);
  const bg = doc.bg || (mat.opaque ? (mat.bg.startsWith("#") ? mat.bg : "#ffffff") : null);
  if (bg) {
    ctx.fillStyle = bg; ctx.strokeStyle = bg; ctx.lineJoin = "round"; ctx.lineWidth = doc.bleed * 2;
    if (doc.bleed > 0) ctx.stroke(path);
    ctx.fill(path);
  }
  ctx.save(); ctx.clip(path, "nonzero");
  await drawLayers(ctx, doc);
  if (doc.border?.width > 0) { ctx.lineWidth = doc.border.width * 2; ctx.strokeStyle = doc.border.color; ctx.stroke(path); }
  ctx.restore();
  if (cutLine) { ctx.setLineDash([0.08, 0.06]); ctx.lineWidth = 0.02; ctx.strokeStyle = "#ff00c8"; ctx.stroke(path); }
  ctx.restore();
};

export const renderSticker = async (doc, dpi = 150, opts = {}) => {
  const tile = tileBox(doc), cv = document.createElement("canvas");
  cv.width = Math.max(1, Math.round(tile.w * dpi)); cv.height = Math.max(1, Math.round(tile.h * dpi));
  const ctx = cv.getContext("2d");
  ctx.__ppi = dpi;
  ctx.scale(dpi, dpi);
  await loadFonts(doc);
  await drawTile(ctx, doc, opts);
  return cv;
};

const specLine = (doc, lay) => `${doc.name} · ${typeOf(doc).name} · ${fmtIn(doc.w)} × ${fmtIn(doc.h)} · ${materialOf(doc).name} · ${(FINISHES.find((f) => f[0] === doc.finish) || [])[1] || ""} · ${lay.cells.length} per sheet × ${lay.sheets} sheet${lay.sheets > 1 ? "s" : ""} = ${doc.copies} · bleed ${fmtIn(doc.bleed)} · Xpattern Sticker Studio`;

// The print sheet: tiles in the grid + registration squares + a spec line in the top margin.
export const renderSheet = async (doc, dpi = 300) => {
  const lay = sheetLayout(doc), S = lay.sheet, cv = document.createElement("canvas");
  cv.width = Math.round(S.w * dpi); cv.height = Math.round(S.h * dpi);
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.scale(dpi, dpi);
  await loadFonts(doc);
  const tileCv = await renderSticker(doc, dpi);
  lay.cells.forEach((c) => {
    ctx.save(); ctx.translate(c.x, c.y);
    if (c.rot) { ctx.translate(c.w, 0); ctx.rotate(Math.PI / 2); }
    ctx.drawImage(tileCv, 0, 0, lay.tile.w, lay.tile.h);
    ctx.restore();
  });
  ctx.fillStyle = "#000";
  regMarks(S).forEach((m) => ctx.fillRect(m.x, m.y, m.s, m.s));
  ctx.save(); ctx.scale(1 / dpi, 1 / dpi);
  ctx.font = `${0.11 * dpi}px "IBM Plex Sans", sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = "#333";
  ctx.fillText(specLine(doc, lay), (S.w / 2) * dpi, 0.19 * dpi);
  ctx.restore();
  return { canvas: cv, layout: lay };
};

// Cut file: the contour of every tile on the sheet + the same registration squares, in real inches.
export const cutSvg = (doc) => {
  const lay = sheetLayout(doc), S = lay.sheet, loops = cutLoops(doc), tile = lay.tile;
  const d = loopsPath(loops);
  const tiles = lay.cells.map((c) => `<path d="${d}" transform="translate(${c.x} ${c.y})${c.rot ? ` translate(${c.w} 0) rotate(90)` : ""} translate(${-tile.x} ${-tile.y})"/>`).join("\n    ");
  const marks = regMarks(S).map((m) => `<rect x="${m.x}" y="${m.y}" width="${m.s}" height="${m.s}" fill="#000"/>`).join("\n    ");
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${S.w}in" height="${S.h}in" viewBox="0 0 ${S.w} ${S.h}">
  <desc>${specLine(doc, lay)} — cut lines (CutContour) + registration marks</desc>
  <g id="RegMarks">
    ${marks}
  </g>
  <g id="CutContour" fill="none" stroke="#ff00c8" stroke-width="0.01">
    ${tiles}
  </g>
</svg>`;
};

export const download = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 4000); };
const toBlob = (cv, type = "image/png", q) => new Promise((res) => cv.toBlob(res, type, q));
const safe = (s) => (s || "sticker").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

export const exportPrintPng = async (doc) => { const { canvas } = await renderSheet(doc, 300); download(await toBlob(canvas), `${safe(doc.name)}-print-300dpi.png`); };
export const exportProofPng = async (doc) => { const cv = await renderSticker(doc, 150, { cutLine: true }); download(await toBlob(cv), `${safe(doc.name)}-proof.png`); };
export const exportCutSvg = (doc) => download(new Blob([cutSvg(doc)], { type: "image/svg+xml" }), `${safe(doc.name)}-cut.svg`);
export const exportPrintPdf = async (doc) => {
  const { canvas, layout } = await renderSheet(doc, 300);
  const blob = await toBlob(canvas);
  const pdf = await api.stickerPdf(blob, layout.sheet.w, layout.sheet.h);
  download(pdf, `${safe(doc.name)}-print.pdf`);
};
export const thumbnail = async (doc) => { const cv = await renderSticker(doc, Math.max(8, 160 / Math.max(tileBox(doc).w, tileBox(doc).h))); return cv.toDataURL("image/png"); };
